import React, { useMemo, useState } from 'react';
import { Progress, Space, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import humanizeUtil from '@utils/humanizeUtil';
import { getMetricShortName } from '@src/utils/metrics';
import { BarGrid, BarNameLabel, BarValueLabel, BarWrapper } from './styled';
import { useWidget } from '../../state/dashboard';
import { useCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';
import useFetchQoEData from './useFetchQoEData';
import { supportedMetricsSelector } from '@src/store/common/selectors';
import { Retry } from '@src/components/common';
import RemoveWidget from '../../control/RemoveWidget';
import Drilldown from '../../control/Drilldown';
import useDiagDashboard from '../../hooks/useDiagDashboard';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import OpportunitiesTrend from './OpportunitiesTrend';
import { LineChartOutlined } from '@ant-design/icons';
import { IMPROVEMENT_OPPORTUNITY_METRICS } from '@src/utils/kpi';
import { StyledSwitch } from '../styled';
import { DEFAULT_METRIC, SPI_QUERY_METRICS } from '../../utils/spi';
import useSelectMetric from '../../hooks/useSelectMetric';

const OpportunitiesWidget = ({ wid }) => {
  const widgetHandler = useWidget({ wid });
  const currentKpi = useCurrentKpiConfig();
  const [showTrend, setShowTrend] = useState(false);
  const supportedMetrics: string[] = useSelector(supportedMetricsSelector);

  const allowedMetrics = SPI_QUERY_METRICS.filter((option) => option in supportedMetrics);

  const { isFetching, isError, data, refetch, queued } = useFetchQoEData({
    wid,
    metrics: allowedMetrics,
    currentKpi,
  });

  const totalsData = data?.totals;

  const { createDashboard } = useDiagDashboard({
    metrics: SPI_QUERY_METRICS,
    defaultMetric: DEFAULT_METRIC.opportunities,
    title: widgetHandler.meta.title,
  });

  const handleToggle = () => {
    setShowTrend((value) => !value);
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title={widgetHandler.meta.title}
      style={{ gridRow: 'span 14' }}
      extra={
        <Space>
          <Tooltip title="Toggle View">
            <StyledSwitch
              size="small"
              checkedChildren={<LineChartOutlined />}
              unCheckedChildren={<LineChartOutlined />}
              checked={showTrend}
              onChange={handleToggle}
            />
          </Tooltip>
          <Drilldown createDashboard={createDashboard} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      <Retry
        isLoading={isFetching}
        showError={isError}
        fetchData={refetch}
        progress={true}
        queued={queued}
      >
        <div>
          {showTrend ? (
            <OpportunitiesTrend data={data?.data} />
          ) : (
            <OpportunitiesBar totals={totalsData} />
          )}
        </div>
      </Retry>
    </WidgetCardWrapper>
  );
};

function OpportunitiesBar({ totals = {} }) {
  const selectMetric = useSelectMetric();

  const handleClickFn = (metric) => {
    return () => {
      selectMetric(metric);
    };
  };

  const sortedTotals = useMemo(() => {
    const data = IMPROVEMENT_OPPORTUNITY_METRICS.map((metric) => ({
      metric,
      value: totals[metric],
    }));
    return data.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }, [totals]);

  return (
    <div style={{ flex: 1, marginTop: 10 }}>
      <BarGrid>
        {sortedTotals.map(({ metric, value: rawValue }) => {
          const { humanizedNumber, humanizedUnit } = humanizeUtil(rawValue, metric, '%');
          return (
            <BarWrapper key={metric} onClick={handleClickFn(metric)}>
              <BarValueLabel>
                {humanizedNumber} {humanizedUnit}
              </BarValueLabel>
              <BarNameLabel>{getMetricShortName({ metricKey: metric })}</BarNameLabel>
              <Progress
                style={{ flexGrow: 1 }}
                percent={rawValue}
                strokeColor={'#c32828'}
                showInfo={false}
              />
            </BarWrapper>
          );
        })}
      </BarGrid>
    </div>
  );
}

export default OpportunitiesWidget;
